import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useNavigate } from 'react-router-dom';
import ChangePasswordModal from '../components/ChangePasswordModal';
import DeleteUserModal from '../components/DeleteUserModal';
import EmailGeneratorModal from '../components/EmailGeneratorModal';
import ModeIndicator from '../components/ModeIndicator';
import LiveWorkRedirectNotice from '../components/LiveWorkRedirectNotice';
import ActivityMetrics from '../components/admin/ActivityMetrics';
import { useMode } from '../contexts/ModeContext';

function AdminDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const { currentMode, modeConfig, isTrainingMode, forceReferenceMode } = useMode();
  const [books, setBooks] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [error, setError] = useState('');
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [qaError, setQaError] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [userActionLoading, setUserActionLoading] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [activeConversation, setActiveConversation] = useState(null);
  const [followUpQuestion, setFollowUpQuestion] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailQaItem, setEmailQaItem] = useState(null);
  const [showLiveWorkNotice, setShowLiveWorkNotice] = useState(false);

  useEffect(() => {
    fetchBooks();
    fetchHistory();
    fetchAllUsers();
  }, []);

  const fetchBooks = async () => {
    try {
      // Only fetch contract/union books for admin dashboard
      const response = await axios.get(`${API}/books/list?category=contract`);
      setBooks(response.data);
    } catch (err) {
      console.error('Error fetching books:', err);
    }
  };

  const handleDownloadBook = (bookId, filename) => {
    const token = localStorage.getItem('token');
    fetch(`${API}/book/download/${bookId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    .then(response => response.blob())
    .then(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'document.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    })
    .catch(err => alert('Failed to download book'));
  };

  const handleDeleteBook = async (bookId) => {
    if (!window.confirm('Are you sure you want to delete this book? This will also delete all associated Q&A data.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/delete-book/${bookId}`);
      setBooks(books.filter(b => b.id !== bookId));
      alert('Book deleted successfully');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete book');
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/qa/history`);
      setHistory(response.data);
    } catch (err) {
      console.error('Error fetching history:', err);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/all-users`);
      setAllUsers(response.data);
    } catch (err) {
      console.error('Error fetching all users:', err);
    }
  };

  const handleRemoveUser = async (userId) => {
    setUserActionLoading(userId);
    try {
      await axios.delete(`${API}/admin/remove-user/${userId}`);
      setAllUsers(allUsers.filter(u => u.id !== userId));
      alert('User removed successfully');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to remove user');
      throw err;
    } finally {
      setUserActionLoading(null);
    }
  };

  const openDeleteModal = (user) => {
    setUserToDelete(user);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
      setError('');
      setUploadSuccess(false);
    } else {
      setError('Please select a valid PDF file');
      setSelectedFile(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setError('');
    setUploadSuccess(false);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('category', 'contract');  // Admin dashboard uploads contract books

      const response = await axios.post(`${API}/admin/upload-book`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadSuccess(true);
      setSelectedFile(null);
      fetchBooks();
      fetchHistory();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload book. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleAskQuestion = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;

    setQaError('');
    setLoading(true);

    try {
      let response;
      
      if (isTrainingMode) {
        // Training Mode endpoint
        response = await axios.post(`${API}/training/ask`, {
          question: question.trim()
        });
        
        // Check for live-work detection (hard stop response)
        if (response.data.mode === 'TRAINING MODE — HARD STOP') {
          // Auto-redirect to Reference Mode
          forceReferenceMode('live_work_detected');
          setShowLiveWorkNotice(true);
        }
        
        // Add mode info to the response
        response.data.responseMode = 'training';
      } else {
        // Reference Mode endpoint
        response = await axios.post(`${API}/qa/ask`, {
          question: question.trim(),
          category: "contract"
        });
        
        // Add mode info to the response
        response.data.responseMode = 'reference';
      }

      setHistory([response.data, ...history]);
      setActiveConversation(response.data);
      setQuestion('');
    } catch (err) {
      setQaError(err.response?.data?.detail || 'Failed to get answer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFollowUp = async (e) => {
    e.preventDefault();
    if (!followUpQuestion.trim() || !activeConversation) return;

    setQaError('');
    setLoading(true);

    try {
      let response;
      const contextualQuestion = `Follow-up question based on previous discussion about "${activeConversation.question}": ${followUpQuestion.trim()}`;
      
      if (isTrainingMode) {
        // Training Mode endpoint
        response = await axios.post(`${API}/training/ask`, {
          question: contextualQuestion
        });
        
        // Check for live-work detection
        if (response.data.mode === 'TRAINING MODE — HARD STOP') {
          forceReferenceMode('live_work_detected');
          setShowLiveWorkNotice(true);
        }
        
        response.data.responseMode = 'training';
      } else {
        // Reference Mode endpoint
        response = await axios.post(`${API}/qa/ask`, {
          question: contextualQuestion,
          category: "contract"
        });
        
        response.data.responseMode = 'reference';
      }

      setHistory([response.data, ...history]);
      setActiveConversation(response.data);
      setFollowUpQuestion('');
    } catch (err) {
      setQaError(err.response?.data?.detail || 'Failed to get answer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const openEmailGenerator = (qaItem) => {
    setEmailQaItem(qaItem);
    setShowEmailModal(true);
  };

  const startNewConversation = () => {
    setActiveConversation(null);
    setFollowUpQuestion('');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <div className="dashboard" data-testid="admin-dashboard">
      {/* Live Work Redirect Notice */}
      {showLiveWorkNotice && (
        <LiveWorkRedirectNotice onDismiss={() => setShowLiveWorkNotice(false)} />
      )}
      
      <header className="dashboard-header">
        <div className="dashboard-logo">
          <img 
            src="/stewie_front.png"
            alt="Stewie Logo"
            className="dashboard-logo-image"
            style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', objectPosition: 'center 25%' }}
          />
          Ask Stewie
        </div>
        <div className="dashboard-user">
          {/* Mode Indicator - Always Visible, Right Aligned */}
          <ModeIndicator showSwitchButtons={true} user={user} />
          <button
            className="btn-nav"
            onClick={() => navigate('/ai-assistant')}
            data-testid="ai-assistant-nav"
          >
            🤖 AI Assistant
          </button>
          <button
            className="btn-nav btn-training"
            onClick={() => navigate('/training/knowledge-check')}
            data-testid="knowledge-check-nav"
          >
            📚 Knowledge Check
          </button>
          <button
            className="btn-nav"
            onClick={() => navigate('/ask-stewie')}
            data-testid="ask-stewie-nav"
          >
            🔍 Ask Stewie
          </button>
          <button
            className="btn-nav"
            onClick={() => navigate('/report-issue')}
            data-testid="report-issue-nav"
          >
            📝 Report Issue
          </button>
          <button
            className="btn-nav"
            onClick={() => navigate('/job-briefing')}
            data-testid="job-briefing-nav"
          >
            🦺 Job Briefing
          </button>
          <button
            className="btn-nav"
            onClick={() => navigate('/fact-finder')}
            data-testid="fact-finder-nav"
          >
            📋 Fact Finder + Statement
          </button>
          <button
            className="btn-nav"
            onClick={() => navigate('/user-guide')}
            data-testid="user-guide-nav"
          >
            📖 Help
          </button>
          <button
            className="btn-nav btn-api-keys"
            onClick={() => navigate('/admin/api-keys')}
            data-testid="api-keys-nav"
          >
            🔑 API Keys
          </button>
          <button
            className="btn-nav btn-analytics"
            onClick={() => navigate('/admin/analytics-new')}
            data-testid="analytics-new-nav"
          >
            📊 Analytics
          </button>
          <div className="user-info">
            <div className="user-name" data-testid="admin-name">{user.name}</div>
            <div className="user-role" data-testid="admin-role">Administrator</div>
          </div>
          <button 
            className="btn-change-password"
            onClick={() => setShowChangePassword(true)}
            data-testid="change-password-btn"
          >
            🔐 Change Password
          </button>
          <button className="btn-logout" onClick={onLogout} data-testid="logout-button">
            Logout
          </button>
        </div>
      </header>

      <div className="dashboard-content">

        {/* Activity Metrics (P2c modernization) */}
        <ActivityMetrics />

        {/* Admin Tools Quick Links */}
        <div className="admin-tools-section" data-testid="admin-tools">
          <h2 className="upload-header"><span></span> Admin Tools</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px', marginBottom: '24px' }}>
            <button
              className="btn-nav"
              onClick={() => navigate('/admin/pattern-intelligence')}
              data-testid="pattern-intelligence-nav"
              style={{ background: '#0b1120', border: '1px solid #334155', color: '#e2e8f0', padding: '14px 16px', borderRadius: '10px', cursor: 'pointer', textAlign: 'left', fontSize: '13px', fontWeight: 500 }}
            >
              Union Brain (Pattern Intelligence)
            </button>
            <button
              className="btn-nav"
              onClick={() => navigate('/admin/document-governance')}
              data-testid="doc-governance-nav"
              style={{ background: '#0b1120', border: '1px solid #334155', color: '#e2e8f0', padding: '14px 16px', borderRadius: '10px', cursor: 'pointer', textAlign: 'left', fontSize: '13px', fontWeight: 500 }}
            >
              Document Governance
            </button>
            <button
              className="btn-nav"
              onClick={() => navigate('/admin/source-status')}
              data-testid="source-status-nav"
              style={{ background: '#0b1120', border: '1px solid #334155', color: '#e2e8f0', padding: '14px 16px', borderRadius: '10px', cursor: 'pointer', textAlign: 'left', fontSize: '13px', fontWeight: 500 }}
            >
              Source Status (current / replaced)
            </button>
            <button
              className="btn-nav"
              onClick={() => navigate('/admin/review-queue')}
              data-testid="review-queue-nav"
              style={{ background: '#0b1120', border: '1px solid #334155', color: '#e2e8f0', padding: '14px 16px', borderRadius: '10px', cursor: 'pointer', textAlign: 'left', fontSize: '13px', fontWeight: 500 }}
            >
              Review Queue
            </button>
            <button
              className="btn-nav"
              onClick={() => navigate('/admin/grievance-intelligence')}
              data-testid="case-intelligence-nav"
              style={{ background: '#0b1120', border: '1px solid #334155', color: '#e2e8f0', padding: '14px 16px', borderRadius: '10px', cursor: 'pointer', textAlign: 'left', fontSize: '13px', fontWeight: 500 }}
            >
              Grievance Case Intelligence
            </button>
            <button
              className="btn-nav"
              onClick={() => navigate('/admin/vocabulary')}
              data-testid="vocabulary-admin-nav"
              style={{ background: '#0b1120', border: '1px solid #334155', color: '#e2e8f0', padding: '14px 16px', borderRadius: '10px', cursor: 'pointer', textAlign: 'left', fontSize: '13px', fontWeight: 500 }}
            >
              Vocabulary Trainer
            </button>
            <button
              className="btn-nav"
              onClick={() => navigate('/admin/retrieval-compare')}
              data-testid="retrieval-compare-nav"
              style={{ background: '#0b1120', border: '1px solid #6366f1', color: '#c4b5fd', padding: '14px 16px', borderRadius: '10px', cursor: 'pointer', textAlign: 'left', fontSize: '13px', fontWeight: 500 }}
            >
              Retrieval A/B Compare
            </button>
          </div>
        </div>

        <div className="upload-section">
          <h2 className="upload-header">
            <span>📤</span>
            Upload Union Contract Document
          </h2>

          {error && <div className="error-message" data-testid="upload-error">{error}</div>}
          {uploadSuccess && (
            <div className="success-message" data-testid="upload-success">
              Book uploaded successfully! The book is now available for Q&A.
            </div>
          )}

          <div
            className="upload-area"
            onClick={() => document.getElementById('file-input').click()}
            data-testid="upload-area"
          >
            <div className="upload-icon">📄</div>
            <div className="upload-text">Click to select a PDF file</div>
            <div className="upload-hint">Only PDF files are supported</div>
          </div>

          <input
            type="file"
            id="file-input"
            className="file-input"
            accept=".pdf"
            onChange={handleFileSelect}
            data-testid="file-input"
          />

          {selectedFile && (
            <div className="selected-file" data-testid="selected-file">
              <span className="file-name">
                <span>📎</span>
                {selectedFile.name}
              </span>
              <button
                className="btn-upload"
                onClick={handleUpload}
                disabled={uploading}
                data-testid="upload-button"
              >
                {uploading ? 'Uploading...' : 'Upload Book'}
              </button>
            </div>
          )}
        </div>

        <div className="upload-section" data-testid="all-users-section">
          <h2 className="upload-header">
            <span>👥</span>
            Registered Users ({allUsers.length})
          </h2>
          {allUsers.length > 0 ? (
            <div className="users-table">
              <table className="users-table-content">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Department</th>
                    <th>Status</th>
                    <th>Registered</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allUsers.map((usr) => (
                    <tr key={usr.id} data-testid={`user-row-${usr.id}`}>
                      <td data-testid="user-name">{usr.name}</td>
                      <td data-testid="user-email">{usr.email}</td>
                      <td data-testid="user-department">{usr.department}</td>
                      <td>
                        {usr.is_admin ? (
                          <span className="badge badge-admin" data-testid="user-status">Admin</span>
                        ) : (
                          <span className="badge badge-approved" data-testid="user-status">User</span>
                        )}
                      </td>
                      <td className="date-cell">{formatDate(usr.created_at)}</td>
                      <td>
                        {!usr.is_admin && (
                          <button
                            className="btn-remove-user"
                            onClick={() => openDeleteModal(usr)}
                            disabled={userActionLoading === usr.id}
                            data-testid={`remove-user-${usr.id}`}
                          >
                            {userActionLoading === usr.id ? '...' : 'Remove Access'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="no-users-message">No users registered yet</p>
          )}
        </div>

        {books.length > 0 && (
          <div className="books-library-section" data-testid="books-library">
            <h2 className="section-title">
              <span>📚</span>
              Know Your Contract ({books.length})
            </h2>
            <div className="books-grid">
              {books.map((book) => (
                <div key={book.id} className="book-card admin-book-card" data-testid={`book-card-${book.id}`}>
                  <div 
                    className="book-card-image"
                    onClick={() => handleDownloadBook(book.id, book.filename)}
                    data-testid={`download-book-${book.id}`}
                  >
                    <img 
                      src="https://customer-assets.emergentagent.com/job_book-whisperer-1/artifacts/5ardfsrk_image.jpeg"
                      alt="Union Logo"
                      className="book-thumbnail"
                    />
                    <div className="download-overlay">
                      <span className="download-icon">📥</span>
                      <span className="download-text">Download</span>
                    </div>
                  </div>
                  <div className="book-card-info">
                    <h3 className="book-card-title" data-testid="book-title">{book.title}</h3>
                    <div className="book-card-meta">
                      {formatDate(book.upload_date)}
                    </div>
                    <div className="book-card-meta">
                      {book.chunk_count} sections
                    </div>
                    <button
                      className="btn-delete-book"
                      onClick={() => handleDeleteBook(book.id)}
                      data-testid={`delete-book-${book.id}`}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {books.length > 0 && (
          <div className={`qa-section ${currentMode}-mode`}>
            <h2 className={`qa-header ${currentMode}-mode`}>
              <img 
                src="/stewie_front.png"
                alt="Stewie"
                className="stewie-icon"
              />
              {isTrainingMode ? 'Ask Stewie — Training Mode' : 'Ask Stewie'}
            </h2>

            <div className="qa-input-container">
              {qaError && <div className="error-message" data-testid="qa-error">{qaError}</div>}
              <form onSubmit={handleAskQuestion}>
                <div className="qa-input-wrapper">
                  <input
                    type="text"
                    className={`qa-input ${currentMode}-mode`}
                    placeholder={modeConfig.placeholder}
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    disabled={loading}
                    data-testid="admin-question-input"
                  />
                  <button
                    type="submit"
                    className={`btn-ask-stewie ${currentMode}-mode`}
                    disabled={loading || !question.trim()}
                    data-testid="admin-ask-button"
                  >
                    <img 
                      src="/stewie_front.png"
                      alt="Stewie"
                      className="button-stewie-icon"
                    />
                    {loading ? 'Asking...' : (isTrainingMode ? 'Ask Training' : 'Ask Stewie')}
                  </button>
                </div>
              </form>
            </div>

            <div className="qa-history" data-testid="admin-qa-history">
              {/* Active Conversation Actions */}
              {activeConversation && (
                <div className="conversation-actions">
                  <div className="active-qa-card">
                    {/* Show mode label for the answer */}
                    {activeConversation.responseMode === 'training' && (
                      <div className="training-answer-label">📚 TRAINING MODE — Educational Example</div>
                    )}
                    {activeConversation.responseMode === 'reference' && (
                      <div className="reference-answer-label">🔵 REFERENCE MODE — Live Work Safe</div>
                    )}
                    <div className="qa-question">
                      <span>❓</span>
                      {activeConversation.question}
                    </div>
                    <div className="qa-answer">{activeConversation.answer}</div>
                  </div>
                  
                  <div className="action-buttons">
                    <div className="follow-up-section">
                      <form onSubmit={handleFollowUp} className="follow-up-form">
                        <input
                          type="text"
                          className="follow-up-input"
                          placeholder="Ask a follow-up question..."
                          value={followUpQuestion}
                          onChange={(e) => setFollowUpQuestion(e.target.value)}
                          disabled={loading}
                        />
                        <button
                          type="submit"
                          className="btn-follow-up"
                          disabled={loading || !followUpQuestion.trim()}
                        >
                          {loading ? '...' : '💬 Follow Up'}
                        </button>
                      </form>
                    </div>
                    
                    <div className="conversation-action-row">
                      <button
                        className="btn-generate-email"
                        onClick={() => openEmailGenerator(activeConversation)}
                      >
                        📧 Generate Email
                      </button>
                      <button className="btn-new-conversation" onClick={startNewConversation}>
                        🔄 New Question
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Q&A History */}
              {!activeConversation && (
                <>
                  {history.length > 0 ? (
                    history.map((item, index) => (
                      <div key={index} className="qa-item" data-testid={`admin-qa-item-${index}`}>
                        <div className="qa-question">
                          <span>❓</span>
                          {item.question}
                        </div>
                        <div className="qa-answer" data-testid={`admin-qa-answer-${index}`}>{item.answer}</div>
                        <div className="qa-item-footer">
                          <div className="qa-timestamp">{formatDate(item.timestamp)}</div>
                          <div className="qa-item-actions">
                            <button 
                              className="btn-continue-chat"
                              onClick={() => setActiveConversation(item)}
                            >
                              💬 Continue
                            </button>
                            <button 
                              className="btn-email-small"
                              onClick={() => openEmailGenerator(item)}
                            >
                              📧
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="empty-history" data-testid="empty-history">
                      <p>No questions asked yet. Start by asking a question about the book!</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
      <ChangePasswordModal 
        isOpen={showChangePassword} 
        onClose={() => setShowChangePassword(false)} 
      />

      <DeleteUserModal
        isOpen={userToDelete !== null}
        onClose={() => setUserToDelete(null)}
        onConfirm={handleRemoveUser}
        user={userToDelete}
      />

      <EmailGeneratorModal
        isOpen={showEmailModal}
        onClose={() => {
          setShowEmailModal(false);
          setEmailQaItem(null);
        }}
        qaItem={emailQaItem}
      />
    </div>
  );
}

export default AdminDashboard;
